import React, { useCallback } from 'react';

// eslint-disable-next-line no-restricted-imports
import { type ExperienceAttributes } from '@confluence/experience-tracker/entry-points/ExperienceEvent.ts';
import { getExperienceTracker } from '@confluence/experience-tracker/entry-points/getExperienceTracker.ts';
import { containsSessionExpiredError } from '@confluence/network';
import { LoadableAfterPaint } from '@confluence/loadable';

import type { ErrorComponentModalProps } from './ErrorComponent.tsx';
import type { GenericErrorBoundaryProps, ErrorComponentProps } from './GenericErrorBoundary.tsx';
import { GenericErrorBoundary } from './GenericErrorBoundary.tsx';

// Error component brings in modal-dialog. It should only be loaded when there is actually an error.
export const ErrorComponent = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-ErrorComponent" */ './ErrorComponent.tsx'))
			.ErrorComponent,
});

export type ErrorBoundaryProps = React.PropsWithChildren<
	{
		attributes?: ExperienceAttributes;
		markErrorCriticalInSSR?: boolean;
	} & ErrorComponentModalProps &
		Pick<GenericErrorBoundaryProps, 'attribution'>
>;

export const ErrorBoundary = ({
	attribution,
	attributes,
	children,
	modal,
	markErrorCriticalInSSR = false,
}: ErrorBoundaryProps) => {
	const renderOnError = useCallback(
		(props: ErrorComponentProps) => <ErrorComponent {...props} modal={modal} />,
		[modal],
	);

	const failExperiencesOnError = useCallback(
		(error: Error) => {
			if (error.name === 'ChunkLoadError')
				getExperienceTracker().abort({ reason: 'ChunkLoadErrorAbort' });
			// the error UI rendered by this component is scary enough for the user
			// to consider all experiences as failures (we prompt the user to contact support and stuff).
			// Session expiration check that you see here isn't excessive, as it is only
			// supposed to be temporary (while we are in the process of transitioning to better error
			// handling via ErrorDisplay components and the like).
			// Once that migration is complete, this failure will be __unconditional__.
			if (!containsSessionExpiredError(error)) {
				getExperienceTracker().fail({
					error,
					attributes: {
						...attributes,
						errorAttribution: attribution,
					},
				});

				if (
					// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
					process.env.REACT_SSR &&
					markErrorCriticalInSSR
				) {
					window.__SSR_HAS_CRITICAL_ERROR__ = true;
				}
			}
		},
		[attributes, attribution, markErrorCriticalInSSR],
	);

	return (
		<GenericErrorBoundary
			attribution={attribution}
			renderOnError={renderOnError}
			onError={failExperiencesOnError}
		>
			{children}
		</GenericErrorBoundary>
	);
};
